<template>
  <v-sheet elevation="2" class="px-12 py-8 rounded white">
    <div class="mb-6">
      <p class="h6 pa-0 mb-0">
        {{ $t('settings.situation_types'), }}
      </p>
      <p
        class="mt-2 pa-0 grey--text text--darken-2"
        v-html="
          $sanitize($t('organization_settings.situation_types_subtitle'), {
            allowedTags: ['a'],
          })
        "
      />
    </div>
    <v-form ref="situationTypes">
      <v-row>
        <v-col cols="5">
          <v-autocomplete
            outlined
            required
            dense
            item-text="text"
            data-testid="situationTypes-select-situation"
            item-value="id"
            v-model="ruleToAdd.situationType"
            :label="`${$t('organization_settings.situation_type')}*`"
            :items="situationTypesOptions"
            :placeholder="$t('common.select')"
          ></v-autocomplete>
        </v-col>
        <v-col cols="5">
          <v-select
            outlined
            required
            dense
            item-text="text"
            item-value="isVisible"
            data-testid="situationTypes-select-status"
            v-model="ruleToAdd.isVisible"
            :label="`${$t('common.status')}*`"
            :placeholder="$t('common.select')"
            :items="status"
          >
          </v-select>
        </v-col>
        <v-col cols="2">
          <Button
            size="large"
            color="primary"
            data-testid="situationTypes-button-add"
            :loading="isLoading"
            :disabled="shouldDisableAddRuleButton"
            :text="$t('common.button.add')"
            @click="createSituationTypeRule"
          />
        </v-col>
      </v-row>
    </v-form>
    <div class="mt-4" v-if="rules.length">
      <p class="h6 mb-3 mt-0">
        {{ $t('organization_settings.added_rules') }}
      </p>
      <template v-for="(rule, index) in rules">
        <v-hover v-slot:default="{ hover }" :key="rule.id">
          <div
            v-if="rule.isViewMode"
            data-testid="situationTypes-div-addedRules"
            class="pa-2 d-flex justify-space-between align-center"
            :class="{
              hover: hover && rule.isViewMode,
              grey: hover && rule.isViewMode,
              'lighten-5': hover && rule.isViewMode,
            }"
          >
            <p
              class="ma-0"
              :data-testid="`situationTypes-paragraph-addedRule${index}`"
            >
              {{ rule.text }}
            </p>
            <div class="d-flex align-center justify-space-between">
              <v-icon
                v-for="icon in view_mode_icons"
                class="grey--text icon"
                :data-testid="`situationTypes-icon-${icon.type}${index}`"
                :key="icon.type"
                :disabled="disableActions"
                @click="handleClick(icon.type, rule)"
              >
                {{ icon.name }}
              </v-icon>
            </div>
          </div>
          <div v-else class="pa-2 d-flex justify-space-between align-center">
            <v-text-field
              outlined
              dense
              hide-details
              disabled
              class="flex-grow-1 flex-shrink-0"
              :data-testid="`situationTypes-textField-situationType${index}`"
              :value="getSituationTypeName(rule)"
              :label="$t('organization_settings.situation_type')"
            ></v-text-field>
            <v-select
              outlined
              dense
              hide-details
              class="mx-4"
              item-text="text"
              item-value="isVisible"
              v-model="rule.isVisible"
              :items="status"
              :data-testid="`situationTypes-select-statusEdit${index}`"
              :label="$t('common.status')"
            ></v-select>
            <div class="d-flex me-5">
              <v-icon
                v-for="icon in edit_mode_icons"
                class="grey--text icon"
                :data-testid="`situationTypes-icon-${icon.type}${index}`"
                :loading="isSaving"
                :key="icon.type"
                @click="handleClick(icon.type, rule)"
              >
                {{ icon.name }}
              </v-icon>
            </div>
          </div>
        </v-hover>
      </template>
    </div>
    <DeleteConfirmationModal
      confirmTestID="situationTypes-delete-button"
      :show="showDeleteModal"
      :isLoading="isLoading"
      :title="$t('organization_settings.delete_rule_title')"
      :text="$t('organization_settings.delete_confirmation')"
      @cancel="showDeleteModal = false"
      @delete="removeRule"
    />
  </v-sheet>
</template>
<script>
import mixin from '@/mixins/mixin.vue'
import { mapActions } from 'vuex'
import _ from 'lodash'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'
import Button from '@/components/UI/Button'

export default {
  name: 'SituationTypes',

  mixins: [mixin],

  components: { DeleteConfirmationModal, Button },

  props: {
    organizationId: {
      required: true,
      type: String,
    },
  },

  data() {
    return {
      isSaving: false,
      isLoading: true,
      showDeleteModal: false,
      situationTypesCatalog: [],
      ruleToAdd: { situationType: '', isVisible: null },
      rules: [],
      disableActions: false,
      status: [
        {
          isVisible: true,
          text: this.$t('organization_settings.enabled_for_all'),
        },
        {
          isVisible: false,
          text: this.$t('organization_settings.disabled_for_all'),
        },
      ],
      view_mode_icons: [
        { type: 'edit', name: 'create' },
        { type: 'delete', name: 'mdi-delete-outline' },
      ],
      edit_mode_icons: [
        { type: 'save', name: 'done' },
        { type: 'cancel', name: 'clear' },
      ],
    }
  },

  async mounted() {
    await this.getSituationTypesCatalog()
    await this.getOrganizationRules()
  },

  computed: {
    shouldDisableAddRuleButton() {
      return Boolean(
        this.isLoading ||
          this.ruleToAdd.isVisible === null ||
          !this.ruleToAdd.situationType
      )
    },

    situationTypesOptions() {
      const filteredCatalog = this.situationTypesCatalog.filter(
        this.getSituationTypesWithoutRule
      )
      const groupByType = _.groupBy(filteredCatalog, 'type')
      const typesKeys = [...new Set(filteredCatalog.map(item => item.type))]
      const orderedByType = typesKeys.map(type => groupByType[type])
      const typesObjects = typesKeys.map(item => {
        return {
          type: item,
          header: this.$t(`quick_action.situation_types.${item}`).toUpperCase(),
        }
      })

      typesKeys.forEach((type, index) =>
        this.insertAt(orderedByType, index + 1 * index, typesObjects[index])
      )

      return _.flatten(orderedByType)
    },
  },

  methods: {
    ...mapActions({
      getCatalog: 'situationTypes/getOrganizationCatalog',
      getRules: 'situationTypes/getOrganizationRules',
      createRule: 'situationTypes/createOrganizationRule',
      updateRule: 'situationTypes/updateOrganizationRule',
      deleteRule: 'situationTypes/deleteOrganizationRule',
    }),

    async removeRule() {
      try {
        this.isLoading = true
        await this.deleteRule({
          organizationId: this.organizationId,
          ruleId: this.ruleToDelete.id,
        })
        this.showSuccess(
          this.$t('organization_settings.rule_removed_successfully')
        )
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        await this.getOrganizationRules()
        this.showDeleteModal = false
        this.isLoading = false
      }
    },

    async getSituationTypesCatalog() {
      try {
        this.isLoading = true
        const response = await this.getCatalog({
          organizationId: this.organizationId,
        })

        const catalog = response
        this.situationTypesCatalog = this.formatSituationTypes(catalog)
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    async getOrganizationRules() {
      try {
        this.isLoading = true

        const response = await this.getRules({
          organizationId: this.organizationId,
        })

        this.rules = response.data
        this.rules = this.formatRules()
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
      }
    },

    async createSituationTypeRule() {
      this.isLoading = true
      try {
        await this.createRule({
          organizationId: this.organizationId,
          rule: this.ruleToAdd,
        })
        this.ruleToAdd = { situationType: '', isVisible: null }
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.isLoading = false
        this.getOrganizationRules()
      }
    },

    async updateSituationTypeRule(rule) {
      try {
        this.isSaving = true
        await this.updateRule({
          organizationId: this.organizationId,
          ruleId: rule.id,
          rule,
        })
      } catch (err) {
        this.handleRequestError(err)
        rule.isVisible = !rule.isVisible
      } finally {
        this.isSaving = false
        this.disableActions = false
        this.getOrganizationRules()
      }
    },

    handleClick(type, rule) {
      switch (type) {
        case 'edit':
          this.disableActions = true
          this.ruleToEdit = rule
          rule.isViewMode = false
          break
        case 'delete':
          this.ruleToDelete = rule
          this.showDeleteModal = true
          break
        case 'save':
          this.updateSituationTypeRule(rule)
          break
        case 'cancel':
          this.isSaving = true
          rule = this.ruleToEdit
          rule.isViewMode = true
          this.disableActions = false
          this.isSaving = false
          break
      }
    },

    insertAt(array, index, ...elements) {
      array.splice(index, 0, ...elements)
    },

    getSituationTypesWithoutRule(situation) {
      const situationTypesIds = this.rules.map(rule => rule.situationType)

      return !situationTypesIds.includes(situation.id)
    },

    getTextToShow(rule) {
      const situation = this.situationTypesCatalog.find(
        situation => rule.situationType === situation.id
      )

      return `${this.$t(
        `quick_action.situation_types.situations.${situation?.name}`
      )} - ${
        rule?.isVisible
          ? _.capitalize(this.$t('organization_settings.enabled_for_all'))
          : _.capitalize(this.$t('organization_settings.disabled_for_all'))
      }`
    },

    getSituationTypeName(rule) {
      const situation = this.situationTypesCatalog.find(
        situation => rule.situationType === situation.id
      )
      return this.$t(
        `quick_action.situation_types.situations.${situation.name}`
      )
    },

    formatSituationTypes(catalog) {
      return _.chain(catalog)
        .map(situation => ({
          ...situation,
          text: this.$t(
            `quick_action.situation_types.situations.${situation.name}`
          ),
        }))
        .orderBy('type', 'asc')
        .value()
    },

    formatRules() {
      const rules = _.chain(this.rules)
        .map(rule => ({
          ...rule,
          isViewMode: true,
          text: this.getTextToShow(rule),
        }))
        .orderBy('text', 'asc')
        .value()

      return rules
    },
  },
}
</script>
<style lang="scss" scoped>
.hover {
  border-radius: 4px;
}
.icon {
  &:last-child:not(:first-child) {
    margin-left: 20px !important;
  }
}
</style>
